import { useCallback, useEffect, useState } from "react";
import { Editor, Range, Path, Element } from "slate";
import { ReactEditor } from "slate-react";
import { useTranslation } from "react-i18next";
import { Wand2 } from "lucide-react";
import { Autographics } from "./Autographics";
import { AITemplateContent } from "../types/ai-templates";
import { useCreateTemplateFromArticleGenerator } from "@packages/api/madlenAPI";

interface ParagraphIndicatorProps {
  editor: Editor;
  elementRef: HTMLElement | null;
  setLoadingRange: (range: Range | null) => void;
  saveArticle: () => Promise<void>;
}

export function ParagraphIndicator({
  editor,
  elementRef,
  setLoadingRange,
  saveArticle,
}: ParagraphIndicatorProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isSelected, setIsSelected] = useState(false);
  const [targetPaths, setTargetPaths] = useState<Path[]>([]);
  const [indicatorStyle, setIndicatorStyle] = useState<{
    top: number;
    height: number;
  }>({ top: 0, height: 0 });
  const [showAutographics, setShowAutographics] = useState(false);
  const [aiResult, setAiResult] = useState<{
    template_contents: { [key: string]: AITemplateContent };
  } | null>(null);
  const [targetPath, setTargetPath] = useState<Path | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { t } = useTranslation();
  const {
    mutateAsync: createTemplateFromArticle,
    isPending: isCreatingTemplate,
  } = useCreateTemplateFromArticleGenerator();

  const updateIndicatorPosition = useCallback(
    (y?: number) => {
      if (!elementRef) {
        console.warn("elementRef is null, cannot update indicator position");
        return;
      }

      const { selection } = editor;
      let currentPaths: Path[] = [];
      let newIsSelected = false;
      let newIsHovered = false;

      if (selection && Range.isExpanded(selection)) {
        const domRange = window.getSelection()?.getRangeAt(0);
        if (domRange) {
          const rect = domRange.getBoundingClientRect();
          const parentRect = elementRef.getBoundingClientRect();
          setIndicatorStyle({
            top: rect.top - parentRect.top,
            height: rect.height,
          });
          newIsSelected = true;
          const selectedBlocks = Array.from(
            Editor.nodes(editor, {
              at: selection,
              match: (n) =>
                !Editor.isEditor(n) &&
                Element.isElement(n) &&
                Editor.isBlock(editor, n) &&
                n.type !== "ai-content",
            }),
          );
          currentPaths = selectedBlocks.map(([, path]) => path);
        }
      } else if (typeof y === "number") {
        const parentRect = elementRef.getBoundingClientRect();
        const elements = elementRef.querySelectorAll<HTMLElement>(
          '[data-slate-node="element"]',
        );
        let hoveredElement: HTMLElement | null = null;
        let hoveredElementPath: Path | null = null;

        for (const element of elements) {
          const rect = element.getBoundingClientRect();
          const elementTop = rect.top - parentRect.top;
          const elementBottom = elementTop + rect.height;

          if (y >= elementTop && y <= elementBottom) {
            hoveredElement = element;
            break;
          }
        }

        if (hoveredElement) {
          const rect = hoveredElement.getBoundingClientRect();
          setIndicatorStyle({
            top: rect.top - parentRect.top,
            height: rect.height,
          });
          newIsHovered = true;
          try {
            const slateNode = ReactEditor.toSlateNode(editor, hoveredElement);
            hoveredElementPath = ReactEditor.findPath(editor, slateNode);
          } catch (error) {
            console.error("Error finding path for hovered element:", error);
          }
        }
        currentPaths = hoveredElementPath ? [hoveredElementPath] : [];
      }

      setIsSelected(newIsSelected);
      setIsHovered(newIsHovered);
      setTargetPaths(currentPaths);
    },
    [editor, elementRef],
  );

  useEffect(() => {
    const handleSelectionChange = () => updateIndicatorPosition();
    document.addEventListener("selectionchange", handleSelectionChange);
    return () =>
      document.removeEventListener("selectionchange", handleSelectionChange);
  }, [updateIndicatorPosition]);

  useEffect(() => {
    if (elementRef) {
      updateIndicatorPosition();
      const timer = setTimeout(() => updateIndicatorPosition(), 100);
      return () => clearTimeout(timer);
    }
  }, [elementRef, editor, updateIndicatorPosition]);

  useEffect(() => {
    if (!elementRef) return;

    const handleEditorMouseMove = (e: MouseEvent) => {
      if (!isSelected) {
        const parentRect = elementRef.getBoundingClientRect();
        const y = e.clientY - parentRect.top;
        updateIndicatorPosition(y);
      }
    };

    const handleEditorMouseLeave = () => {
      if (!isSelected) {
        setIsHovered(false);
        setTargetPaths([]);
      }
    };

    elementRef.addEventListener("mousemove", handleEditorMouseMove);
    elementRef.addEventListener("mouseleave", handleEditorMouseLeave);

    return () => {
      elementRef.removeEventListener("mousemove", handleEditorMouseMove);
      elementRef.removeEventListener("mouseleave", handleEditorMouseLeave);
    };
  }, [elementRef, isSelected, updateIndicatorPosition]);

  const handleIndicatorMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!elementRef || isSelected) return;
      const parentRect = elementRef.getBoundingClientRect();
      const y = e.clientY - parentRect.top;
      updateIndicatorPosition(y);
    },
    [elementRef, updateIndicatorPosition, isSelected],
  );

  const handleIndicatorMouseLeave = useCallback(() => {
    if (!isSelected) {
      setIsHovered(false);
      setTargetPaths([]);
    }
  }, [isSelected]);

  const handleAIButtonClick = useCallback(async () => {
    let rangeToLoad: Range | null = null;
    let textToProcess = "";
    let insertPath: Path | null = null;
    setShowAutographics(false);
    if (editor.selection && Range.isExpanded(editor.selection)) {
      rangeToLoad = editor.selection;
      textToProcess = Editor.string(editor, rangeToLoad);
      const [, endPoint] = Range.edges(rangeToLoad);
      insertPath = Path.next(Path.parent(endPoint.path));
    } else if (targetPaths.length > 0) {
      try {
        const firstPath = targetPaths[0];
        rangeToLoad = Editor.range(editor, firstPath);
        textToProcess = Editor.string(editor, rangeToLoad);
        insertPath = Path.next(firstPath);
      } catch (e) {
        console.error("Error getting range for target path:", e);
      }
    }

    if (!rangeToLoad || !textToProcess || !insertPath) {
      console.warn("Cannot trigger AI: No valid range or text found.");
      setError(t("errors.noTextSelected"));
      return;
    }

    setLoadingRange(rangeToLoad);
    setTargetPath(insertPath);
    setError(null);

    try {
      const articleId = window.location.pathname.split("/").pop();
      if (!articleId) {
        throw new Error("No article ID found");
      }

      console.log("🚀 Sending text to backend:", textToProcess);
      const result = await createTemplateFromArticle({
        id: parseInt(articleId),
        data: { selectedText: textToProcess },
      });

      console.log("📥 Backend response:", result);

      // ✅ FIXED: Proper backend response handling
      let templateContents: { [key: string]: AITemplateContent } = {};

      if (result && typeof result === "object") {
        console.log("🔍 Analyzing result structure:", Object.keys(result));

        // Method 1: Check for template_contents (plural)
        if (
          result.template_contents &&
          typeof result.template_contents === "object"
        ) {
          templateContents = result.template_contents;
          console.log(
            "✅ Found template_contents (plural):",
            Object.keys(templateContents),
          );
        }
      }

      console.log("🎯 Final processed template contents:", templateContents);
      console.log("📊 Template count:", Object.keys(templateContents).length);

      // Always try to open Autographics, even with empty templates for debugging
      if (Object.keys(templateContents).length > 0) {
        console.log(
          "✅ Opening Autographics with templates:",
          Object.keys(templateContents),
        );
        setAiResult({ template_contents: templateContents });
        setShowAutographics(true);
      } else {
        console.warn(
          "⚠️ No valid templates found, but still opening Autographics for debugging",
        );
        setAiResult({ template_contents: {} });
        setShowAutographics(true);
        setError("No templates received from backend");
      }
    } catch (error) {
      console.error("❌ Error creating AI template:", error);
      setError("Error processing AI request");

      // Even on error, open Autographics for debugging
      setAiResult({ template_contents: {} });
      setShowAutographics(true);
    } finally {
      setLoadingRange(null);
    }
  }, [editor, targetPaths, setLoadingRange, createTemplateFromArticle, t]);

  const handleAutographicsClose = useCallback(() => {
    console.log("🚪 Closing Autographics");
    setShowAutographics(false);
    setAiResult(null);
    setTargetPath(null);
    setError(null);
  }, []);

  const handleSelectTemplate = useCallback(
    (template: string, content: AITemplateContent) => {
      console.log("🎯 Selected template:", template, content);
      // Template seçildiğinde otomatik kaydet
      saveArticle()
        .then(() => {
          console.log("✅ Article saved after template selection");
        })
        .catch((error) => {
          console.error("❌ Error saving article:", error);
        });
    },
    [saveArticle],
  );

  if (!elementRef) return null;

  const isIndicatorVisible = isHovered || isSelected;

  return (
    <div
      className="absolute left-0 top-0 w-8 h-full z-[99999] pointer-events-none"
      onMouseMove={handleIndicatorMouseMove}
      onMouseLeave={handleIndicatorMouseLeave}
    >
      <div
        className={`
          absolute left-0 flex items-center pointer-events-auto
          transition-all duration-300 ease-in-out
          ${isIndicatorVisible ? "opacity-100" : "opacity-0"}
        `}
        style={{
          top: `${indicatorStyle.top}px`,
          height: `${indicatorStyle.height || 24}px`,
        }}
      >
        <div
          className={`
            w-1.5 h-full rounded-r
            ${isSelected ? "bg-primary-500" : "bg-gray-300"}
            transition-colors duration-300
          `}
        />
        <button
          className={`
            group absolute left-full p-1.5 ml-1 min-w-6 min-h-6 rounded-full
            border border-primary-300/40 dark:border-primary-700/40
            bg-gradient-to-tr from-fuchsia-500 via-violet-500 to-indigo-500
            hover:from-fuchsia-400 hover:via-indigo-500 hover:to-violet-600
            transition-all duration-300 ease-in-out transform hover:scale-110
            backdrop-blur-sm
            ring-2 ring-white/40 dark:ring-white/20
            flex items-center justify-center overflow-hidden
            ${isIndicatorVisible ? "opacity-100 scale-100 translate-x-0" : "opacity-0 scale-75 -translate-x-2"}
            ${isCreatingTemplate ? "opacity-80 cursor-not-allowed" : ""}
          `}
          style={{ top: "50%", transform: "translateY(-50%)" }}
          onClick={handleAIButtonClick}
          aria-label="Insert AI content"
          disabled={isCreatingTemplate}
        >
          {isCreatingTemplate ? (
            <svg
              className="animate-spin w-4 h-4 text-white"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
          ) : (
            <>
              <Wand2 className="w-4 h-4 z-10 text-white drop-shadow-sm" />
              <span className="relative z-10 group-hover:mr-2 max-w-0 group-hover:max-w-[120px] whitespace-nowrap overflow-hidden transition-all duration-300 opacity-0 group-hover:opacity-100 ml-0 group-hover:ml-2 text-sm font-semibold text-white">
                {t("articleGenerator.enhanceContent")}
              </span>
            </>
          )}
        </button>
      </div>
      {error && (
        <div className="absolute left-10 top-2 bg-red-500 text-white text-xs p-2 rounded max-w-48 z-20">
          {error}
        </div>
      )}
      {showAutographics && aiResult && targetPath && (
        <Autographics
          editor={editor}
          aiResult={aiResult}
          targetPath={targetPath}
          onClose={handleAutographicsClose}
          onSelectTemplate={handleSelectTemplate}
        />
      )}
    </div>
  );
}
